import {
  AuthUser,
  SignInOutput,
  signIn as ampSignIn,
  signOut as ampSignOut,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  updateMFAPreference,
} from 'aws-amplify/auth';

import { PropsWithChildren, useMemo, useState } from 'react';
import { setAmplitudeUser, unsetAmplitudeUser } from 'src/libs/amp';
import { AuthContext, CurrentAuthState, MfaMethod, UserProfile } from '.';
import { getUserProfile } from './utils';
import { setSentryUser } from 'src/libs/sentry';

type AuthError = {
  name: string;
  message?: string;
};

const isAuthError = (error: unknown): error is AuthError => {
  return 'name' in (error as AuthError);
};

const MFA_CODE_ERROR_NAMES = [
  'CodeMismatchException',
  'EnableSoftwareTokenMFAException',
];

export function AuthContextProvider(props: PropsWithChildren) {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const [currentAuthState, setCurrentAuthState] = useState<CurrentAuthState>(
    CurrentAuthState.Loading,
  );

  // Transient state for the MFA steps of the sign-in flow
  const [mfaAllowedTypes, setMfaAllowedTypes] = useState<MfaMethod[] | null>(
    null,
  );
  const [mfaCodeDestination, setMfaCodeDestination] = useState<string | null>(
    null,
  );
  const [totpSetupUri, setTotpSetupUri] = useState<string | null>(null);
  const [totpSetupSecret, setTotpSetupSecret] = useState<string | null>(null);
  // Username of the sign-in in progress, used to label the TOTP setup QR code
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);

  function clearMfaState() {
    setMfaAllowedTypes(null);
    setMfaCodeDestination(null);
    setTotpSetupUri(null);
    setTotpSetupSecret(null);
    setPendingUsername(null);
  }

  const getAndSetUserProfile = async (user: AuthUser) => {
    const { tokens } = await fetchAuthSession();

    if (!tokens) {
      signOut();
      return;
    }

    const currentUserProfile = await getUserProfile(user, tokens);
    setSentryUser(user);
    setAmplitudeUser(user, currentUserProfile?.role ?? null);

    setUserProfile(currentUserProfile);
    setCurrentAuthState(CurrentAuthState.SignedIn);
  };

  /**
   * This function is design to refresh the Context's
   * when needed (usually when the page is reloaded).
   * It will use the Amplify functions to check the current
   * authentication state of the user and synchronise this
   * context with that.
   */
  async function refreshAuthState() {
    try {
      const { tokens } = await fetchAuthSession();
      if (!tokens) {
        setUserProfile(null);
        setCurrentAuthState(CurrentAuthState.SignedOut);
        return;
      }
      const nextCognitoUser = await getCurrentUser();
      await getAndSetUserProfile(nextCognitoUser);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error in refreshAuth, signing out', error);
      signOut();
    }
  }

  /**
   * Routes every signIn/confirmSignIn result to the matching auth state.
   * With MFA enabled, Cognito chains challenges (e.g. forced password
   * change -> MFA code), so all steps must flow through this single router.
   */
  async function handleSignInNextStep(
    nextStep: SignInOutput['nextStep'],
    username: string | null,
  ) {
    switch (nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setCurrentAuthState(CurrentAuthState.ForcePasswordChange);
        return;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        setMfaAllowedTypes(
          nextStep.allowedMFATypes?.filter(
            (type): type is MfaMethod => type === 'EMAIL' || type === 'TOTP',
          ) ?? null,
        );
        setCurrentAuthState(CurrentAuthState.MfaSelection);
        return;
      case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
        setMfaCodeDestination(
          nextStep.codeDeliveryDetails?.destination ?? null,
        );
        setCurrentAuthState(CurrentAuthState.MfaEmailCode);
        return;
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setCurrentAuthState(CurrentAuthState.MfaTotpCode);
        return;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
        // Email MFA needs no setup, so the only method to set up is TOTP
        const result = await confirmSignIn({ challengeResponse: 'TOTP' });
        await handleSignInNextStep(result.nextStep, username);
        return;
      }
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        const { sharedSecret, getSetupUri } = nextStep.totpSetupDetails;
        setTotpSetupSecret(sharedSecret);
        setTotpSetupUri(
          getSetupUri('Merkatus', username ?? undefined).toString(),
        );
        setCurrentAuthState(CurrentAuthState.MfaTotpSetup);
        return;
      }
      case 'DONE':
      default: {
        clearMfaState();
        const user = await getCurrentUser();
        await getAndSetUserProfile(user);
      }
    }
  }

  async function signIn(username: string, password: string) {
    try {
      const signInResult = await ampSignIn({ username, password });
      setPendingUsername(username);
      await handleSignInNextStep(signInResult.nextStep, username);
    } catch (error) {
      if (!isAuthError(error)) {
        throw error;
      }

      if (error.name === 'UserNotFoundException') {
        error.message = 'Invalid username or password';
      }
      if (error.name === 'UserAlreadyAuthenticatedException') {
        signOut();
      }

      throw error;
    }
  }

  async function completeNewPassword(newPassword: string) {
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });
      await handleSignInNextStep(result.nextStep, pendingUsername);
    } catch (error) {
      if (!isAuthError(error)) {
        throw error;
      }
      if (error.name === 'UserNotFoundException') {
        error.message = 'Invalid username or password';
      }
      throw error;
    }
  }

  async function selectMfaType(type: MfaMethod) {
    const result = await confirmSignIn({ challengeResponse: type });
    await handleSignInNextStep(result.nextStep, pendingUsername);
  }

  async function confirmMfaCode(code: string) {
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      await handleSignInNextStep(result.nextStep, pendingUsername);
    } catch (error) {
      if (isAuthError(error) && MFA_CODE_ERROR_NAMES.includes(error.name)) {
        error.message = 'Invalid verification code. Please try again.';
      }
      throw error;
    }
  }

  async function confirmTotpSetupDuringSignIn(code: string) {
    try {
      const result = await confirmSignIn({ challengeResponse: code });
      if (result.nextStep.signInStep === 'DONE') {
        // Keep email MFA enabled alongside the newly registered TOTP so both
        // methods are selectable at the next sign-in
        await updateMFAPreference({ totp: 'ENABLED', email: 'ENABLED' });
      }
      await handleSignInNextStep(result.nextStep, pendingUsername);
    } catch (error) {
      if (isAuthError(error) && MFA_CODE_ERROR_NAMES.includes(error.name)) {
        error.message = 'Invalid verification code. Please try again.';
      }
      throw error;
    }
  }

  async function cancelMfa() {
    await signOut();
  }

  /**
   * This function is required so that the Forgot/Reset Password
   * flow is entered. This function is Async to keep the API for these
   * functions consistent and to make it easy to add Async congito auth
   * actions in the future.
   */
  async function forgotPasswordStart() {
    setCurrentAuthState(CurrentAuthState.ForgotPassword);
  }

  async function cancelForgotPassword() {
    setCurrentAuthState(CurrentAuthState.SignedOut);
  }

  async function forgotPasswordSendCode(username: string) {
    try {
      await resetPassword({ username });
      setCurrentAuthState(CurrentAuthState.PasswordReset);
      // This promise does not return anything. In order to retain the username
      // we must manually create a temporary  CognitoUser object.
      // This allows for the 'username' to be automatically filled for a user in
      // the UpdatePassword screen.
    } catch (error) {
      if (!isAuthError(error)) {
        throw error;
      }
      if (error.name === 'UserNotFoundException') {
        error.message = 'Invalid username or password';
      }
      if (error.message?.startsWith('Cannot reset password for the user')) {
        error.message = 'Contact support to reset your password';
      }
      throw error;
    }
  }

  async function forgotPasswordUpdatePassword(
    username: string,
    code: string,
    newPassword: string,
  ) {
    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
      // Attempt sign in with new password to complete the reset process
      await signIn(username, newPassword);
    } catch (error) {
      if (!isAuthError(error)) {
        throw error;
      }
      if (error.name === 'UserNotFoundException') {
        error.message = 'Invalid username';
      }
      throw error;
    }
  }

  async function signOut() {
    try {
      await ampSignOut();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('Error in signout', e);
    } finally {
      unsetAmplitudeUser();
      setSentryUser(null);
      setUserProfile(null);
      clearMfaState();
      setCurrentAuthState(CurrentAuthState.SignedOut);
    }
  }

  const value = useMemo(() => {
    return {
      refreshAuthState,
      signIn,
      completeNewPassword,
      forgotPasswordStart,
      cancelForgotPassword,
      forgotPasswordSendCode,
      forgotPasswordUpdatePassword,
      selectMfaType,
      confirmMfaCode,
      confirmTotpSetupDuringSignIn,
      cancelMfa,
      signOut,
      userProfile,
      currentAuthState,
      mfaAllowedTypes,
      mfaCodeDestination,
      totpSetupUri,
      totpSetupSecret,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userProfile,
    currentAuthState,
    mfaAllowedTypes,
    mfaCodeDestination,
    totpSetupUri,
    totpSetupSecret,
  ]);

  return (
    <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>
  );
}
